import React, { useState, useEffect } from 'react';
import { Typography, Box, Paper, Button } from '@mui/material';
import { useAccount } from '../../contexts/AccountContext';
import { api } from '../../utils/api';
import {
    Table,
    TableBody,
    TableHead,
    TableHeader,
    TableRow,
    TableCell,
} from "../ui/table";
import DownloadIcon from '@mui/icons-material/Download';

export default function PatronInfo() {
    const [patrons, setPatrons] = useState([]);
    const { currentAccountId } = useAccount();
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(true);
    const [isDownloading, setIsDownloading] = useState(false);

    useEffect(() => {
        const loadData = async () => {
            if (currentAccountId) {
                try {
                    await loadPatrons(currentAccountId);
                } catch (error) {
                    console.error('Error loading patrons:', error);
                    setError(error.message || 'Failed to load patron information');
                }
            }
        };

        loadData();
    }, [currentAccountId]);

    const loadPatrons = async (accountId) => {
        try {
            setLoading(true);
            const response = await api.get(`/api/users/${accountId}/patrons`);
            
            if (Array.isArray(response)) {
                setPatrons(response);
                setError('');
            } else {
                console.error('Invalid response format:', response);
                setError('Invalid data format received from server');
            }
        } catch (error) {
            console.error('Error fetching patrons:', error);
            setError(error.message || 'Failed to load patrons');
        } finally {
            setLoading(false);
        }
    };

    const isMobile = window.innerWidth <= 600;

    const capitalizeFirstLetter = (string) => {
        if (!string || string.trim() === '') return '';
        return string.charAt(0).toUpperCase() + string.slice(1).toLowerCase();
    };

    const handleDownloadCSV = async () => {
        try {
            setIsDownloading(true);
            
            const response = await fetch(`${process.env.REACT_APP_URL_backend}/api/users/${currentAccountId}/pat/download`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token')}`,
                    'Accept': 'text/csv'
                },
            });

            if (!response.ok) {
                throw new Error('Failed to download CSV');
            }

            // Get the filename from the Content-Disposition header
            const contentDisposition = response.headers.get('Content-Disposition');
            const filenameMatch = contentDisposition && contentDisposition.match(/filename="(.+)"/);
            const filename = filenameMatch ? filenameMatch[1] : `patrons_${new Date().toISOString()}.csv`;

            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Error downloading CSV:', error);
            setError('Failed to download CSV file');
        } finally {
            setIsDownloading(false);
        }
    };

    return (
        <div className="max-w-7xl mx-auto px-8 py-6">
            <div className="bg-gray-100 p-4 rounded-2xl shadow-xl overflow-hidden">
                <Box className="p-4 mb-4 bg-white rounded-lg">
                    <div className="flex justify-between items-center mb-4">
                        <Typography variant="h5" className="font-bold" style={{ fontWeight: 700 }}>
                            Patron Information
                        </Typography>
                        <Button
                            variant="contained"
                            color="primary"
                            startIcon={<DownloadIcon />}
                            onClick={handleDownloadCSV}
                            disabled={isDownloading}
                            size="medium"
                        >
                            {isDownloading ? 'Downloading...' : 'Export'}
                        </Button>
                    </div>

                    {error && (
                        <Typography color="error" className="mb-4">
                            {error}
                        </Typography>
                    )}

                    {loading ? (
                        <Typography>Loading...</Typography>
                    ) : patrons.length === 0 ? (
                        <Typography color="textSecondary" className="mt-4">
                            No patron information available.
                        </Typography>
                    ) : (
                        <div className={isMobile ? "patron-table-mobile" : "patron-table-desktop"}>
                            {isMobile ? (
                                patrons.map((patron) => (
                                    <Paper 
                                        key={patron.id} 
                                        className="p-4 mb-4 rounded-lg"
                                    >
                                        <div className="flex flex-col gap-2">
                                            <Typography variant="body1" className="font-medium">
                                                {patron.name}
                                            </Typography>
                                            <div className="text-sm text-gray-600">
                                                <div>Email: {patron.email}</div>
                                                <div>Phone: {patron.phoneNumber}</div>
                                                <div>Age: {patron.age || ''}</div>
                                                <div>Gender: {capitalizeFirstLetter(patron.gender)}</div>
                                            </div>
                                        </div>
                                    </Paper>
                                ))
                            ) : (
                                <Table className="w-full">
                                    <TableHeader>
                                        <TableRow className="bg-muted/50">
                                            <TableHead className="w-[25%] font-bold text-lg">Name</TableHead>
                                            <TableHead className="w-[25%] font-bold text-lg">Email</TableHead>
                                            <TableHead className="w-[20%] font-bold text-lg">Phone</TableHead>
                                            <TableHead className="w-[15%] font-bold text-lg">Age</TableHead>
                                            <TableHead className="w-[15%] font-bold text-lg">Gender</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {patrons.map((patron) => (
                                            <TableRow 
                                                key={patron.id}
                                                className="hover:bg-gray-50"
                                            >
                                                <TableCell className="font-medium">
                                                    {patron.name}
                                                </TableCell>
                                                <TableCell>{patron.email}</TableCell>
                                                <TableCell>{patron.phoneNumber}</TableCell>
                                                <TableCell>{patron.age || ''}</TableCell>
                                                <TableCell>{capitalizeFirstLetter(patron.gender)}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            )}
                        </div>
                    )}
                </Box>
            </div>
        </div>
    );
} 